import { useState } from "react";
import { HardDrive, Info, AlertTriangle, CheckCircle2 } from "lucide-react";

const mb = (b) => b == null ? "—" : b >= 1024 * 1024 * 1024 ? `${(b / 1024 / 1024 / 1024).toFixed(2)} GB` : `${Math.round(b / 1024 / 1024)} MB`;

const TONE = {
  ok: "border-emerald-200 bg-emerald-50 text-emerald-800",
  watch: "border-amber-200 bg-amber-50 text-amber-800",
  act: "border-red-200 bg-red-50 text-[#B01E28]",
};

/**
 * B-0 — the server's own disk, measured by the app.
 *
 * The platform publishes a temp-disk CEILING (1 GB on Starter, 2 GB on Launch) and NO usage
 * figure, and there is no shell into a production pod. Production was evicted twice for
 * exceeding that ceiling and nobody could see it coming. This card is the only way to answer
 * "is the disk still filling up?" — so it reports what this container has written, and nothing
 * here is a guess.
 */
export function ServerDiskCard({ disk, onRefresh }) {
  const [showAll, setShowAll] = useState(false);
  if (!disk) return null;
  const p = disk.parts || {};
  const pc = disk.photo_cache || {};
  const nights = disk.nightly || [];
  const unmanaged = nights.filter((n) => !n.managed);
  const tone = TONE[disk.status] || TONE.ok;
  const shown = showAll ? [...nights].reverse() : [...nights].reverse().slice(0, 6);

  return (
    <div data-testid="server-disk-section" className="border border-slate-200 bg-white rounded-xl shadow-sm mb-5 overflow-hidden">
      <div className="bg-slate-100 px-4 py-2.5 font-mono text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
        <HardDrive className="w-3.5 h-3.5 text-[#B01E28]" />Server disk — what this container has written
        {onRefresh && (
          <button data-testid="disk-refresh" onClick={onRefresh} className="ml-auto normal-case font-sans text-xs text-slate-500 hover:text-[#B01E28] transition-colors">Refresh</button>
        )}
      </div>

      <div className="p-4">
        <div data-testid="disk-footprint" data-tone={disk.status} className={`rounded-lg border ${tone} px-3 py-2.5 mb-4 flex items-center gap-2 text-sm font-semibold`}>
          {disk.status === "ok" ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertTriangle className="w-4 h-4 shrink-0" />}
          <span>This container is using <span data-testid="disk-own-bytes">{mb(disk.own_footprint_bytes)}</span> of temp disk
          {disk.pct_of_limit != null ? ` — ${disk.pct_of_limit}% of the ${mb(disk.limit_bytes)} limit you set` : ""}.</span>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          {[
            { k: "nightly", label: "Nightly local copies", v: mb(p.nightly_local_backups_bytes), sub: `${p.nightly_local_backups_count} folder(s)` },
            { k: "uploads", label: "Photo cache", v: mb(p.uploads_folder_bytes), sub: pc.cap_bytes ? `of a ${mb(pc.cap_bytes)} cap · ${pc.files ?? 0} file(s)` : `${pc.files ?? 0} file(s)` },
            { k: "scratch", label: "Backup scratch", v: mb(p.backup_scratch_bytes), sub: `${(disk.scratch || []).length} in progress / left behind` },
            { k: "logs", label: "Log files", v: mb(p.logs_bytes), sub: "rotated by the platform" },
          ].map((row) => (
            <div key={row.k} data-testid={`disk-part-${row.k}`} className="rounded-lg border border-slate-200 p-3">
              <div className="font-mono text-[10px] uppercase tracking-wider text-slate-400">{row.label}</div>
              <div className="text-lg font-semibold text-slate-800 mt-0.5">{row.v}</div>
              <div className="text-xs text-slate-500">{row.sub}</div>
            </div>
          ))}
        </div>

        {pc.cap_bytes ? (
          <div data-testid="disk-photo-cache" className="flex items-start gap-2 text-xs text-slate-600 border border-slate-200 rounded-lg p-3 mb-4">
            <Info className="w-4 h-4 shrink-0 mt-0.5 text-slate-400" />
            <span>
              <b data-testid="disk-photo-cache-headline">Photo cache: {mb(pc.bytes)} of a {mb(pc.cap_bytes)} cap</b>
              {pc.pct_of_cap != null ? ` (${pc.pct_of_cap}%)` : ""} across {pc.files ?? 0} file(s).
              This is the server's own copy of recently-viewed photos, kept so the next look is instant —
              your photos themselves live in B2, and nothing here is a backup of anything. It is the biggest
              thing this container writes, so it is the number to watch when photo delivery changes.
              <span className="text-slate-400"> The cap is set by <code className="font-mono">{pc.cap_env_key}</code>; this card only reports it.</span>
            </span>
          </div>
        ) : null}

        {disk.last_run && (
          <div data-testid="disk-last-run" className="text-xs text-slate-600 border border-slate-200 rounded-lg p-3 mb-4">
            <b>Last off-site backup</b> ({(disk.last_run.created_at || "").slice(0, 16).replace("T", " ")} UTC) needed a peak of{" "}
            <b data-testid="disk-peak-bytes">{mb(disk.last_run.peak_scratch_bytes)}</b> of temp disk and produced a {mb(disk.last_run.archive_bytes)} archive
            {disk.last_run.photos_new != null ? ` · ${disk.last_run.photos_new} new photo(s) copied` : ""}
            {disk.last_run.photos_pending_after ? `, ${disk.last_run.photos_pending_after} still queued for the next run` : ""}.
            {disk.last_run.peak_scratch_bytes == null && <span className="text-slate-400"> (peak not recorded — this run predates the disk fix)</span>}
          </div>
        )}

        <div data-testid="disk-nightly-inventory">
          <div className="font-mono text-[10px] uppercase tracking-wider text-slate-400 mb-2">
            Nightly local copies — inventory ({nights.length})
          </div>
          {nights.length === 0 ? (
            <div className="text-xs text-slate-500">None on this server. (These are wiped on every restart — your real backups are the encrypted copies in B2.)</div>
          ) : (
            <>
              <div className="overflow-x-auto border border-slate-200 rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider">Folder (UTC)</th>
                      <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-wider">Size</th>
                      <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-wider">Of which photos copy</th>
                      <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider">Rotation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((n) => (
                      <tr key={n.name} data-testid={`disk-night-${n.name}`} className="border-t border-slate-100">
                        <td className="px-3 py-1.5 font-mono text-slate-700">{n.name}</td>
                        <td className="px-3 py-1.5 text-right text-slate-700">{mb(n.bytes)}</td>
                        <td className="px-3 py-1.5 text-right text-slate-500">{n.uploads_copy_bytes ? mb(n.uploads_copy_bytes) : "—"}</td>
                        <td className="px-3 py-1.5">
                          {n.managed
                            ? <span className="text-slate-500">auto (keeps newest {disk.local_backup_keep})</span>
                            : <span className="text-amber-700">kept — your decision</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {nights.length > shown.length && !showAll && (
                <button data-testid="disk-show-all" onClick={() => setShowAll(true)}
                        className="mt-2 text-xs text-slate-500 hover:text-[#B01E28] transition-colors">
                  Show all {nights.length} folders (newest first) ↓
                </button>
              )}
              {unmanaged.length > 0 && (
                <div data-testid="disk-unmanaged-note" className="flex items-start gap-2 text-xs text-amber-800 border border-amber-200 bg-amber-50 rounded-lg p-3 mt-2">
                  <Info className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>
                    <b>{unmanaged.length} folder(s) marked "kept — your decision"</b> were created before the disk fix and hold{" "}
                    <b>{mb(unmanaged.reduce((s, n) => s + n.bytes, 0))}</b>. Nothing removes them automatically. Newer folders rotate to the
                    newest {disk.local_backup_keep}. Your off-site copies in B2 are untouched by any of this.
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-start gap-2 text-xs text-slate-500 mt-4">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            This measures what the app itself writes — the only thing that counts toward the pod's temp-disk limit, which the
            platform exposes no meter for. {disk.limit_bytes == null && <>Set <code className="font-mono">{disk.limit_env_key}</code> in the backend
            environment to your tier's ceiling (in MB) and this card will warn at 70%. </>}
            {disk.node_filesystem && <>The host filesystem shows {mb(disk.node_filesystem.free_bytes)} free, but that is the whole machine and is <b>not</b> your headroom.</>}
          </span>
        </div>
      </div>
    </div>
  );
}
